import { useMutation } from "@apollo/client";
import { DotsHorizontalIcon } from "@heroicons/react/outline";
import { withErrorBoundary } from "@sentry/react";
import { Menu, MenuItem } from "@szhsin/react-menu";
import copy from "copy-to-clipboard";
import { useCallback, useContext } from "react";
import {
  ArtifactType,
  DeleteMeetingTranscriptsMutation,
  DeleteMeetingTranscriptsNewPageMutationMutationVariables,
  DeleteMeetingVideoRecordingMutationMutation,
  DeleteMeetingVideoRecordingMutationMutationVariables,
  GetMeetingTranscriptQueryQuery,
  SaveMeetingVideoRecordingMutationMutation,
  SaveMeetingVideoRecordingMutationMutationVariables,
} from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import TranscriptionParticipation from "@apps/meeting-transcription-dialog/participation";
import TranscriptionText from "@apps/meeting-transcription-dialog/transcription-text";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import { canLayoutSidebarBeDisplayed } from "@components/layout/layout-helpers";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  assertNonNull,
  getUrl,
} from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import { MeetingPageContext } from "../meeting-page-context";
import { MeetingSidebarTabEnum } from "../meeting-sidebar/meeting-view-sidebar-constants";
import MeetingNotetakerCheckbox from "../notetaker-checkbox";
import deleteMeetingTranscriptsNewPageMutation from "./graphql/delete-meeting-transcripts-mutation";
import deleteMeetingVideoRecordingMutation from "./graphql/delete-meeting-video-recording-mutation";
import saveMeetingVideoRecordingMutation from "./graphql/save-meeting-video-recording-mutation";
import PrepGroup from "./prep-group";
import MeetingSummaryGroupTranscriptionSuggestedArtifact from "./summary-group-transcription-suggested-artifact";

const ArtifactSummaryGroup = ({
  artifactType,
  meeting,
  meetingGroupId,
}: {
  artifactType: ArtifactType;
  meeting: NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
  meetingGroupId: number;
}) => {
  const label = useLabel();
  const suggestedArtifacts = meeting.transcript?.suggestedArtifacts
    ? assertEdgesNonNull(meeting.transcript.suggestedArtifacts)
    : [];
  const filteredSuggestedArtifacts = suggestedArtifacts.filter(
    (suggestedArtifact) => suggestedArtifact.artifactType === artifactType,
  );
  const createdArtifacts = assertEdgesNonNull(meeting.artifacts).filter(
    (node) => node.id && node.artifactType === artifactType,
  );
  const total = filteredSuggestedArtifacts.length + createdArtifacts.length;

  if (total === 0) return null;

  return (
    <PrepGroup
      title={
        <div className="flex items-center gap-2">
          {label(artifactType, { pluralize: true, titleCase: true })}
          <div className="flex items-center">
            <span className="bg-gray-100 rounded-md px-1.5 py-0.5 font-medium text-xs">
              {total}
            </span>
          </div>
        </div>
      }
      collapsible={true}
      isCollapsedByDefault={false}
    >
      <div className="flex flex-col px-6 py-4 gap-4">
        {createdArtifacts.map((artifact) => (
          <div className="" key={artifact.id}>
            <Artifact
              urlPrefix={getUrl({
                meetingGroupId: meetingGroupId,
                meetingId: meeting.id,
              })}
              artifact={artifact}
            />
          </div>
        ))}
        {filteredSuggestedArtifacts.map((suggestedArtifact) => (
          <MeetingSummaryGroupTranscriptionSuggestedArtifact
            key={suggestedArtifact.id}
            meeting={meeting}
            suggestedArtifact={suggestedArtifact}
          />
        ))}
      </div>
    </PrepGroup>
  );
};

const MeetingSummaryGroupTranscription = ({
  meeting,
  meetingGroupId,
  isCurrentMeeting,
  refetch,
  loading,
}: {
  meeting: NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
  meetingGroupId: number;
  isCurrentMeeting: boolean;
  refetch: () => void;
  loading: boolean;
}) => {
  const {
    setActiveSidebarTab,
    setSidebarMeetingView,
    isInSidebar,
    isInExtension,
  } = useContext(MeetingPageContext);
  const { saveUiPreference } = useUiPreferenceCache();
  const [deleteTranscript] = useMutation<
    DeleteMeetingTranscriptsMutation,
    DeleteMeetingTranscriptsNewPageMutationMutationVariables
  >(deleteMeetingTranscriptsNewPageMutation);
  const [saveVideoRecording, { loading: isSavingVideoRecording }] = useMutation<
    SaveMeetingVideoRecordingMutationMutation,
    SaveMeetingVideoRecordingMutationMutationVariables
  >(saveMeetingVideoRecordingMutation, {
    onCompleted: () => {
      successNotificationVar({
        title: "Video recording is being saved, please check back later.",
      });
      refetch();
    },
    onError: (error) => {
      errorNotificationVar({
        title: "Could not save video recording.",
        description: error.message,
      });
    },
  });
  const [deleteVideoRecording, { loading: isDeletingVideoRecording }] =
    useMutation<
      DeleteMeetingVideoRecordingMutationMutation,
      DeleteMeetingVideoRecordingMutationMutationVariables
    >(deleteMeetingVideoRecordingMutation, {
      onCompleted: () => {
        successNotificationVar({ title: "Video recording deleted." });
        refetch();
      },
      onError: (error) => {
        errorNotificationVar({
          title: "Could not delete video recording.",
          description: error.message,
        });
      },
    });
  const { ConfirmationDialog, confirm } = useConfirm(
    "Delete Transcript",
    "Are you sure you want to delete this transcript? This action cannot be undone.",
  );

  const transcript = meeting.transcript as
    | (NonNullable<typeof meeting.transcript> & {
        videoRecording?: string | null;
        canSaveVideoRecording?: boolean | null;
        videoRecordingAvailableUntil?: string | null;
      })
    | null
    | undefined;
  const orderedTranscriptData = transcript?.orderedTranscriptData;
  const highlightList = transcript?.highlightList
    ? assertEdgesNonNullWithStringId(transcript.highlightList)
    : [];
  const highlightCategories = transcript?.highlightCategories
    ? assertEdgesNonNullWithStringId(transcript.highlightCategories)
    : [];

  const videoRecording = transcript?.videoRecording ?? null;
  const recallVideoRecording = transcript?.recallVideoRecording ?? null;
  const videoSrc = (videoRecording || recallVideoRecording) ?? undefined;
  const canSaveVideoRecording = transcript?.canSaveVideoRecording ?? false;
  const videoRecordingAvailableUntil = transcript?.videoRecordingAvailableUntil
    ? new Date(transcript.videoRecordingAvailableUntil)
    : null;
  const videoAvailableUntilText = videoRecordingAvailableUntil
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(videoRecordingAvailableUntil)
    : null;
  const isVideoFeatureEnabled = waffle.flag_is_active("video-recording");
  const showVideoSection =
    isVideoFeatureEnabled && (videoRecording || canSaveVideoRecording);

  const handleCopyTranscript = useCallback(() => {
    copy(
      assertNonNull(
        meeting?.transcript?.orderedTranscriptData?.map(
          (transcriptData) =>
            `${transcriptData?.speaker}: ${transcriptData?.text}\n`,
        ),
      ).join("\n"),
    );
    successNotificationVar({ title: "Transcript copied.", timeout: 1000 });
  }, [meeting]);

  if (!meeting.organization)
    return (
      <PrepGroup title="Summary">
        <div className="mt-1 fs-mask text-gray-500">
          The meeting is not associated to an existing organization. The notes
          and/or transcript cannot be summarized.
        </div>
      </PrepGroup>
    );

  const handleDeleteTranscript = async () => {
    const confirmed = await confirm();
    if (confirmed) {
      await deleteTranscript({
        variables: {
          meetingId: meeting.id,
        },
      });
    }
  };

  const handleSaveVideoRecording = () => {
    if (isSavingVideoRecording) return;
    saveVideoRecording({
      variables: {
        meetingId: meeting.id,
      },
    });
  };

  const handleDeleteVideoRecording = () => {
    if (isDeletingVideoRecording) return;
    deleteVideoRecording({
      variables: {
        meetingId: meeting.id,
      },
    });
  };

  const handleClickViewPreviousMeeting = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    // If the layout sidebar can be displayed
    // If this view is not rendered in the sidebar or extension
    // we open the sidebar and render the previous meeting in sidebar > meetings tab
    if (
      canLayoutSidebarBeDisplayed() &&
      !isInSidebar &&
      !isInExtension &&
      setActiveSidebarTab &&
      setSidebarMeetingView
    ) {
      e.preventDefault();
      e.stopPropagation();
      saveUiPreference({ meetingSidebarExpanded: true });
      setActiveSidebarTab(MeetingSidebarTabEnum.meetings);
      setSidebarMeetingView(meeting.id ?? "overview");
    }
  };

  return (
    <>
      <ConfirmationDialog />

      <PrepGroup
        title={
          <div className="flex items-center gap-2">
            Summary
            <div className="flex items-center">
              <span className="bg-gray-100 rounded-md p-1 font-medium text-xs">
                <TopicflowIcon type="ai" className="w-3 h-3" />
              </span>
            </div>
          </div>
        }
        topRightContent={
          isCurrentMeeting ? null : (
            <div>
              <AppLink
                to={getUrl({
                  meetingGroupId: meetingGroupId,
                  meetingId: meeting.id,
                })}
                onClick={handleClickViewPreviousMeeting}
                className="text-blue-link hover:underline font-normal"
              >
                Go to Last Meeting
              </AppLink>
            </div>
          )
        }
        collapsible={true}
        isCollapsedByDefault={false}
      >
        <div className="px-6 py-4 flex flex-col gap-6">
          {(meeting.transcript?.summary || meeting.notesSummary) && (
            <div className="flex justify-center">
              <TranscriptionParticipation meeting={meeting} />
            </div>
          )}
          <div className="text-gray-600 whitespace-pre-line fs-mask empty:hidden">
            {highlightList.length > 0 ? (
              <ul className="list-disc mt- space-y-3 mb-4">
                {highlightList.map((node) => (
                  <li key={node.id}>{node.highlight}</li>
                ))}
              </ul>
            ) : highlightCategories.length > 0 ? (
              <ul className="mt-2 space-y-3 mb-4">
                {highlightCategories.map((node) => (
                  <li key={node.id}>
                    <span className="font-semibold text-gray-900">
                      {node.categoryTitle}
                    </span>
                    <ul className="list-disc ml-6 mt-2 space-y-3">
                      {assertEdgesNonNullWithStringId(node.highlights).map(
                        (highlightNode) => (
                          <li key={highlightNode.id}>
                            {highlightNode.highlight}
                          </li>
                        ),
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            ) : meeting.transcript?.summary || meeting.notesSummary ? null : (
              <span>No highlights.</span>
            )}
          </div>
        </div>
      </PrepGroup>

      {[
        ArtifactType.ActionItem,
        ArtifactType.Goal,
        ArtifactType.Recognition,
      ].map((artifactType) => (
        <ArtifactSummaryGroup
          key={artifactType}
          artifactType={artifactType}
          meeting={meeting}
          meetingGroupId={meetingGroupId}
        />
      ))}

      {showVideoSection && (
        <PrepGroup title="Video Recording" collapsible={true}>
          <div className="px-6 py-4 flex flex-col gap-4">
            {videoSrc && (
              <>
                <video
                  key={videoRecording || recallVideoRecording}
                  controls
                  preload="metadata"
                  className="w-full aspect-video rounded-lg border border-gray-200 bg-black fs-exclude"
                  src={videoSrc}
                />

                {videoRecording && (
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      text="Delete Recording"
                      theme={buttonTheme.textRed}
                      onClick={handleDeleteVideoRecording}
                      disabled={isDeletingVideoRecording}
                      tooltip="Remove the saved video from Topicflow"
                    />
                    {isDeletingVideoRecording && (
                      <Loading mini size={5} className="text-gray-500" />
                    )}
                  </div>
                )}
              </>
            )}

            {!videoRecording && canSaveVideoRecording && (
              <>
                <div className="text-sm text-gray-600">
                  Video is not permanently saved, but you may save it now.
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    text="Save Video Recording"
                    theme={buttonTheme.primary}
                    onClick={handleSaveVideoRecording}
                    disabled={isSavingVideoRecording}
                    tooltip="Save this video permanently"
                  />
                  {isSavingVideoRecording && (
                    <Loading mini size={5} className="text-gray-500" />
                  )}
                </div>
                {videoAvailableUntilText && (
                  <div className="text-xs text-gray-500">
                    Available to save until {videoAvailableUntilText}.
                  </div>
                )}
              </>
            )}
          </div>
        </PrepGroup>
      )}

      {/* Transcript */}
      {orderedTranscriptData && orderedTranscriptData.length > 0 ? (
        <>
          <PrepGroup
            title="Transcript"
            collapsible={true}
            isCollapsedByDefault={true}
            topRightContent={
              <Menu
                portal={false}
                className="z-dropdown fs-unmask "
                align="end"
                transition={false}
                aria-label="Transcript dropdown menu list"
                menuButton={
                  <button
                    className="text-gray-400 rounded"
                    data-testid="transcript-dropdown-button"
                    aria-label="Transcript dropdown menu button"
                  >
                    <span className="sr-only">Open options</span>
                    <DotsHorizontalIcon className="h-6 w-6 hover:bg-gray-100" />
                  </button>
                }
              >
                <MenuItem className="text-sm" onClick={handleCopyTranscript}>
                  Copy Transcript
                </MenuItem>
                <MenuItem className="text-sm" onClick={handleDeleteTranscript}>
                  Delete Entire Transcript
                </MenuItem>
              </Menu>
            }
          >
            <div className="px-6 py-1">
              <div className="mt-4 fs-mask">
                <TranscriptionText meeting={meeting} />
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <Button
                  onClick={() => refetch()}
                  disabled={loading}
                  text="Refresh"
                />
                <Loading
                  mini
                  size={5}
                  className={loading ? "opacity-100" : "opacity-0"}
                />
              </div>
            </div>
          </PrepGroup>
        </>
      ) : (
        <PrepGroup title="Transcript">
          <div className="mt-1 fs-mask text-gray-500 py-4 px-6">
            <div>No transcript has been recorded for this meeting.</div>
            {meeting.isRecurring &&
            meeting.meetingGroup?.canUpdate?.permission ? (
              <div className="mt-2 text-gray-800">
                <MeetingNotetakerCheckbox
                  meeting={meeting}
                  className="flex items-center gap-1"
                  label="Add the notetaker to future meetings"
                />
              </div>
            ) : null}
          </div>
        </PrepGroup>
      )}
    </>
  );
};

const MeetingSummaryGroupTranscriptionWithSentry = withErrorBoundary(
  MeetingSummaryGroupTranscription,
  {
    fallback: (
      <Error description={"The meeting summary could not be rendered."} />
    ),
  },
);

export default MeetingSummaryGroupTranscriptionWithSentry;
